import { useRouter } from "next/router";
import { FiltersFieldProps } from "src/components/dashboard/Filters";
import { defaultFilters } from "src/components/dashboard/list";
import React, {
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Company, SearchContext, SectorPanelFilters } from "src/common/search";
import { fetchWrapper, StockTableData } from "src/helpers";
import AppContext from "../AppContext";
import moment from "moment-timezone";
import PushNotificationPrompt from "../PushNotificationPrompt";
import { ThemeProvider } from "./contexts/ThemeContext";
import HighProbabilityAlertModal from "src/common/HighProbabilityAlertModal";
import PlanNotAvailable from "src/common/planNotAvailable";
import RiskAppetiteModal from "src/components/dashboard/RiskAppetiteModal";
import CompleteProfileModal from "src/common/CompleteProfileModal";
import FreeTrialRestrictionModal from "src/common/freeTrialRestrictionModal";
import { hasCmsCheckoutIntent, readCmsPlanId } from "src/helpers/coupon-client";
import TrialExpiryModal from "src/common/TrialExpiryModal";
import DisclaimerModal from "src/common/DisclaimerModal";
import GlobalNotification from "src/common/GlobalNotification";
import HoverNotificationStackWrapper from "src/common/HoverNotificationStackWrapper";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import {
  ACTIVE_TAB_STORAGE_KEY,
  getLastSearchCompanyDataDriven,
  migrateLegacyLastSearchCompanyDataDriven,
  readStockActiveTabFromLocalStorage,
  setLastSearchCompanyDataDriven,
  writeStockActiveTabToLocalStorage,
} from "./stockTabPersistence";

const Header = React.lazy(() => import("./Header"));
const Sidebar = React.lazy(() => import("./Sidebar"));
const MarketTicker = React.lazy(() => import("./MarketTicker"));
import Footer from "./Footer";

interface LayoutProps {
  children: ReactNode;
}

const FILTERS_STORAGE_KEY = "stock_applied_filters";
const SECTOR_PANEL_FILTERS_KEY = "sector_panel_filters";
const SELECTED_MONTH_KEY = "selected_month";

const defaultSectorPanelFilters: SectorPanelFilters = {
  deselectedSectors: [],
  selectedSegment: "All",
  selectedYears: [],
  timeSeries: "15",
  isDerivative: false,
};

const ConsumerNewLayout = (props: LayoutProps) => {
  const { children } = props;
  const context = useContext(AppContext);
  const { currentUser } = context.state;
  const router = useRouter();
  const path = router.pathname;
  const user = getLocalStorageUser() || {};
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [path]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [appliedFilters, setAppliedFiltersState] =
    useState<FiltersFieldProps>(defaultFilters);
  const [activeTab, setActiveTabState] = useState<string>("Derivatives");
  const [sectorPanelFilters, setSectorPanelFiltersState] =
    useState<SectorPanelFilters>(defaultSectorPanelFilters);
  const currentMonthIndex = new Date().getMonth();
  const [selectedMonth, setSelectedMonthState] =
    useState<number>(currentMonthIndex);
  const [isFiltersLoaded, setIsFiltersLoaded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertPlanNotAvailable, setAlertPlanNotAvailable] = useState(false);
  const [alertStockName, setAlertStockName] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [freeTrialRestrictModal, setFreeTrialRestrictModal] = useState(false);
  const defaultValue: Company = { symbol: "", company_name: "" };
  const [selectedCompany, setSelectedCompany] = useState<Company>(defaultValue);
  const [records, setRecords] = useState<StockTableData[]>([]);
  const hasUserSearchedRef = useRef(false);
  const [isFilteredOut, setIsFilteredOut] = useState(false);
  const [activeModal, setActiveModal] = useState<
    | "DISCLAIMER"
    | "COMPLETE_PROFILE"
    | "RISK_APPETITE"
    | "FREE_TRIAL_STARTED"
    | "TRIAL_EXPIRY"
    | null
  >(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [isTrialExpiryReady, setIsTrialExpiryReady] = useState(false);
  const [modalCheckTrigger, setModalCheckTrigger] = useState(0);
  const [isValidatingProfile, setIsValidatingProfile] = useState(false);
  const [hasValidatedProfile, setHasValidatedProfile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !currentUser) return;

    const identifier =
      currentUser.email || currentUser.phone_number || currentUser.uid;
    if (!identifier) return;

    // Use the freshest local storage user for profile checks in case context is stale
    const localUser = getLocalStorageUser() || {};
    const emailToCheck = currentUser.email?.trim() || localUser.email?.trim();
    const customerEmailToCheck =
      currentUser.customer_email?.trim() || localUser.customer_email?.trim();
    const phoneToCheck =
      currentUser.phone_number?.trim() || localUser.phone_number?.trim();
    const riskProfileToCheck =
      localUser.risk_profile || currentUser.risk_profile;

    let targetModal: typeof activeModal = null;

    // 1. Complete Profile — skip during CMS paid checkout and on payment success page.
    // After pay we clear CMS storage; Google users often still lack phone, which would
    // otherwise reopen this modal on /subscription/payment-gateway.
    const cmsPlanId = readCmsPlanId();
    const isCmsPaidCheckout = hasCmsCheckoutIntent() && Number(cmsPlanId) !== 1;
    const isPaymentSuccessPage = path.startsWith(
      "/subscription/payment-gateway",
    );
    // Apple private-relay email counts as present — only collect mobile;
    // backend handles the rest. Do not reopen email step for Hide My Email.
    const hasEmail = Boolean(emailToCheck || customerEmailToCheck);
    const isProfileIncomplete =
      path !== "/profile" &&
      !isPaymentSuccessPage &&
      !isCmsPaidCheckout &&
      (!hasEmail || !phoneToCheck);
    const hasActivePlan = localUser.is_subscribed || localUser.is_free;

    // 2. Disclaimer
    const lastAgreedStr = localStorage.getItem(
      `disclaimer_agreed_timestamp_${identifier}`,
    );
    const lastAgreed = lastAgreedStr ? parseInt(lastAgreedStr, 10) : 0;
    const is30DaysChecked =
      localStorage.getItem(`disclaimer_dont_show_30_days_${identifier}`) ===
      "true";
    const validityPeriod = is30DaysChecked
      ? 30 * 24 * 60 * 60 * 1000
      : 24 * 60 * 60 * 1000;
    const isDisclaimerDue =
      hasActivePlan &&
      path !== "/subscription" &&
      Date.now() - lastAgreed > validityPeriod;

    // 4. Risk Appetite
    const isRiskIncomplete =
      hasActivePlan &&
      path !== "/subscription" &&
      (riskProfileToCheck === undefined || riskProfileToCheck === null);

    if (isProfileIncomplete) {
      targetModal = "COMPLETE_PROFILE";
    } else if (isDisclaimerDue) {
      targetModal = "DISCLAIMER";
    } else if (
      hasActivePlan &&
      path !== "/subscription" &&
      sessionStorage.getItem("showFreeTrialStartedPopup") === "true"
    ) {
      targetModal = "FREE_TRIAL_STARTED";
    } else if (isRiskIncomplete) {
      if (!hasValidatedProfile && !isValidatingProfile && localUser.token) {
        setIsValidatingProfile(true);
        fetchWrapper
          .post(
            "/api/users/validate",
            {},
            { Authorization: `Bearer ${localUser.token}` },
          )
          .then(
            (validateResponse: {
              risk_profile?: string[];
              disclaimer_accepted?: boolean;
            }) => {
              if (validateResponse.risk_profile) {
                const updatedUser = {
                  ...localUser,
                  risk_profile: validateResponse.risk_profile,
                  disclaimer_accepted: validateResponse.disclaimer_accepted,
                };
                localStorage.setItem("BetaUser", JSON.stringify(updatedUser));
                setModalCheckTrigger((prev) => prev + 1); // Re-evaluate queue with new data
              }
            },
          )
          .catch(() => {
            // Error validating, will just show modal on next re-render
          })
          .finally(() => {
            setHasValidatedProfile(true);
            setIsValidatingProfile(false);
          });
        return; // Pause queue while validating
      } else if (hasValidatedProfile) {
        targetModal = "RISK_APPETITE";
      } else {
        return; // Currently validating
      }
    } else if (
      hasActivePlan &&
      path !== "/subscription" &&
      isTrialExpiryReady
    ) {
      targetModal = "TRIAL_EXPIRY";
    }

    if (targetModal !== activeModal) {
      setActiveModal(null);
      if (targetModal) {
        const timer = setTimeout(() => {
          setActiveModal(targetModal);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    path,
    isTrialExpiryReady,
    modalCheckTrigger,
    hasValidatedProfile,
    isValidatingProfile,
  ]);

  const handleDisclaimerAccept = (dontShowFor30Days: boolean = false) => {
    const identifier =
      currentUser?.email || currentUser?.phone_number || currentUser?.uid;
    if (identifier) {
      localStorage.setItem(
        `disclaimer_agreed_timestamp_${identifier}`,
        Date.now().toString(),
      );
      if (dontShowFor30Days) {
        localStorage.setItem(
          `disclaimer_dont_show_30_days_${identifier}`,
          "true",
        );
      } else {
        localStorage.removeItem(`disclaimer_dont_show_30_days_${identifier}`);
      }
    }
    setModalCheckTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    const user = getLocalStorageUser() || {};
    let initialFilters = { ...defaultFilters };

    if (storedFilters) {
      try {
        const parsed = JSON.parse(storedFilters);
        initialFilters = { ...initialFilters, ...parsed };
      } catch (error) {
        console.error("Failed to parse stored filters:", error);
      }
    }

    if (
      !initialFilters.risk_profile ||
      initialFilters.risk_profile.length === 0
    ) {
      if (user.risk_profile && user.risk_profile.length > 0) {
        initialFilters.risk_profile = user.risk_profile;
      }
    }

    setAppliedFiltersState(initialFilters);

    migrateLegacyLastSearchCompanyDataDriven();
    const storedNav = readStockActiveTabFromLocalStorage();
    if (storedNav.tab) {
      setActiveTabState(storedNav.tab);
    }

    const storedSectorFilters = localStorage.getItem(SECTOR_PANEL_FILTERS_KEY);
    if (storedSectorFilters) {
      try {
        const parsed = JSON.parse(storedSectorFilters);
        setSectorPanelFiltersState({ ...defaultSectorPanelFilters, ...parsed });
      } catch (error) {
        console.error("Failed to parse stored sector panel filters:", error);
      }
    }

    const storedMonth = localStorage.getItem(SELECTED_MONTH_KEY);
    if (storedMonth !== null) {
      try {
        const monthIndex = parseInt(storedMonth, 10);
        if (monthIndex >= 0 && monthIndex <= 11) {
          setSelectedMonthState(monthIndex);
        }
      } catch (error) {
        console.error("Failed to parse stored month:", error);
      }
    }

    setIsFiltersLoaded(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let timer: NodeJS.Timeout;

    try {
      const isTrial = user?.is_trial;
      const planExpiryStr = user?.planExpiry;
      if (isTrial === true && planExpiryStr) {
        const expiryDate = moment(planExpiryStr);
        const today = moment().startOf("day");
        const days = expiryDate.startOf("day").diff(today, "days");

        const lastShown = localStorage.getItem("trialExpiryPopupLastShown");
        const todayStr = moment().format("YYYY-MM-DD");
        if (days >= 0 && days <= 7 && lastShown !== todayStr) {
          setTrialDaysLeft(days);

          // Wait 2 minutes before making trial expiry ready in queue
          timer = setTimeout(
            () => {
              setIsTrialExpiryReady(true);
            },
            2 * 60 * 1000,
          );
        }
      }
    } catch (error) {
      console.error("Error checking trial expiry:", error);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !isFiltersLoaded) return;
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(appliedFilters));
  }, [appliedFilters, isFiltersLoaded]);

  useEffect(() => {
    if (typeof window === "undefined" || !isFiltersLoaded) return;
    const current = readStockActiveTabFromLocalStorage();
    writeStockActiveTabToLocalStorage({
      ...current,
      tab: activeTab,
    });
  }, [activeTab, isFiltersLoaded]);

  useEffect(() => {
    if (typeof window === "undefined" || !isFiltersLoaded) return;
    localStorage.setItem(
      SECTOR_PANEL_FILTERS_KEY,
      JSON.stringify(sectorPanelFilters),
    );
  }, [sectorPanelFilters, isFiltersLoaded]);

  useEffect(() => {
    if (typeof window === "undefined" || !isFiltersLoaded) return;
    localStorage.setItem(SELECTED_MONTH_KEY, selectedMonth.toString());
  }, [selectedMonth, isFiltersLoaded]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    migrateLegacyLastSearchCompanyDataDriven();

    if (path === "/analyze-stock") {
      let next: Company = defaultValue;
      const sessionStored = sessionStorage.getItem("lastSearchCompany");
      if (sessionStored) {
        try {
          const parsed = JSON.parse(sessionStored) as Company;
          if (parsed?.symbol) {
            next = {
              symbol: parsed.symbol,
              company_name: parsed.company_name || "",
            };
          }
        } catch {
          /* ignore */
        }
      }
      if (!next.symbol) {
        const fromDd = getLastSearchCompanyDataDriven();
        if (fromDd?.symbol) next = fromDd;
      }
      if (!next.symbol) {
        const u = getLocalStorageUser() || {};
        if (u.latestSymbol) {
          next = { symbol: u.latestSymbol, company_name: "" };
        }
      }
      setSelectedCompany(next);
    } else if (path === "/dashboard") {
      const fromDd = getLastSearchCompanyDataDriven();
      setSelectedCompany(fromDd?.symbol ? fromDd : defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (typeof window === "undefined" || !selectedCompany.symbol) return;

    if (path === "/analyze-stock") {
      if (hasUserSearchedRef.current) {
        sessionStorage.setItem(
          "lastSearchCompany",
          JSON.stringify(selectedCompany),
        );
      }
    } else {
      setLastSearchCompanyDataDriven(selectedCompany);
    }
  }, [selectedCompany, path]);

  useEffect(() => {
    if (!selectedDate) return;

    const dateStringIST = moment(selectedDate)
      .tz("Asia/Kolkata")
      .format("YYYY-MM-DD");
    sessionStorage.setItem("selectedDate", dateStringIST);
  }, [selectedDate]);

  const setAppliedFilters = (newFilters: FiltersFieldProps) => {
    setAppliedFiltersState(newFilters);
  };

  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
  };

  const setSectorPanelFilters = (filters: SectorPanelFilters) => {
    setSectorPanelFiltersState(filters);
  };

  const setSelectedMonth = (month: number) => {
    setSelectedMonthState(month);
  };

  const clearAppliedFilters = () => {
    setAppliedFiltersState(defaultFilters);
    setActiveTabState("Derivatives");
    if (typeof window !== "undefined") {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
      localStorage.removeItem(ACTIVE_TAB_STORAGE_KEY);
    }
  };

  const clearSectorPanelFilters = () => {
    setSectorPanelFiltersState(defaultSectorPanelFilters);
    if (typeof window !== "undefined") {
      localStorage.removeItem(SECTOR_PANEL_FILTERS_KEY);
    }
  };

  const markUserSearched = () => {
    hasUserSearchedRef.current = true;
  };

  const PUBLIC_PATHS = [
    "/",
    "/refund-policy",
    "/shipping-and-delivery-policy",
    "/testimonials",
    "/api-docs",
    "/unsubscribe",
  ];

  if (path === "/unsubscribe") {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-[#18181b] dark:bg-[#18181b] text-gray-100">
          {children}
        </div>
      </ThemeProvider>
    );
  }

  if (!currentUser && PUBLIC_PATHS.includes(path)) {
    if (path === "/") {
      return <>{children}</>;
    }
    return (
      <ThemeProvider>
        <div className="flex flex-col min-h-screen bg-[#edf1fc] dark:bg-[#121419] text-gray-900 dark:text-gray-100">
          <div className="flex-1">{children}</div>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="bg-[#edf1fc] dark:bg-[#121419] text-gray-900 dark:text-gray-100 transition-colors duration-200">
        <div className="flex h-screen">
          <div className="hidden lg:block relative self-stretch flex-shrink-0 lg:min-w-[68px] z-90">
            <Sidebar onItemClick={() => setSidebarOpen(false)} />
          </div>

          <div
            className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <Sidebar onItemClick={() => setSidebarOpen(false)} />
          </div>

          {sidebarOpen && (
            <div
              className="fixed inset-0 backdrop-blur-sm bg-opacity-50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          <SearchContext.Provider
            value={{
              selectedCompany,
              setSelectedCompany,
              setIsLoading,
              isLoading,
              showHighProbabilityAlertModal: (
                stockName: string,
                isFilteredOut?: boolean,
              ) => {
                setAlertStockName(stockName);
                setIsFilteredOut(isFilteredOut || false);
                setAlertModalOpen(true);
              },
              showPlanNotAvailableModal: () => {
                setAlertPlanNotAvailable(true);
              },
              showFreeRestrictionModal: () => {
                setFreeTrialRestrictModal(true);
              },
              selectedDate,
              setSelectedDate,
              markUserSearched,
              appliedFiltersStore: appliedFilters,
              setStoreAppliedFilters: setAppliedFilters,
              clearStoreAppliedFilters: clearAppliedFilters,
              activeTab,
              setActiveTab,
              sectorPanelFilters,
              setSectorPanelFilters,
              clearSectorPanelFilters,
              selectedMonth,
              setSelectedMonth,
              records,
              setRecords,
              isFiltersLoaded,
            }}
          >
            <div className="flex-1 flex flex-col overflow-hidden">
              {(currentUser?.is_subscribed || currentUser?.is_free) && (
                <MarketTicker />
              )}
              <Header
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />

              <main
                ref={mainRef}
                className={`flex-1 flex flex-col ${activeModal === "COMPLETE_PROFILE" ? "overflow-hidden" : "overflow-x-hidden overflow-y-auto"} bg-[#edf1fc] dark:bg-[#121419] relative`}
              >
                <div className="flex-1 px-3 sm:px-3 lg:px-3 py-0 sm:py-0 space-y-6 pb-6">
                  {children}
                </div>
                <CompleteProfileModal
                  isOpen={activeModal === "COMPLETE_PROFILE"}
                  onSuccess={() => setModalCheckTrigger((prev) => prev + 1)}
                />
              </main>
              <Footer />

              <HighProbabilityAlertModal
                isOpen={alertModalOpen}
                onClose={() => setAlertModalOpen(false)}
                stockName={alertStockName}
                selectedDate={selectedDate}
                isFilteredOut={isFilteredOut}
                onNavigateToAnalyzeStock={(symbol) => {
                  setAlertModalOpen(false);
                  sessionStorage.setItem(
                    "lastSearchCompany",
                    JSON.stringify({ symbol }),
                  );
                  void router.push("/analyze-stock");
                }}
              />
              <PlanNotAvailable
                isOpen={alertPlanNotAvailable}
                onClose={() => setAlertPlanNotAvailable(false)}
              />
              <FreeTrialRestrictionModal
                isOpen={freeTrialRestrictModal}
                onClose={() => setFreeTrialRestrictModal(false)}
                variant="analyzeStock"
              />
              <TrialExpiryModal
                isOpen={activeModal === "TRIAL_EXPIRY"}
                daysLeft={trialDaysLeft}
                onClose={() => {
                  setIsTrialExpiryReady(false);
                  localStorage.setItem(
                    "trialExpiryPopupLastShown",
                    moment().format("YYYY-MM-DD"),
                  );
                  setModalCheckTrigger((prev) => prev + 1);
                }}
              />
              <DisclaimerModal
                isOpen={activeModal === "DISCLAIMER"}
                onAccept={handleDisclaimerAccept}
              />
              {activeModal === "RISK_APPETITE" && (
                <RiskAppetiteModal
                  onSave={() => {
                    const u = getLocalStorageUser();
                    const newRiskProfile = u?.risk_profile || [];
                    const updatedFilters = {
                      ...appliedFilters,
                      risk_profile: newRiskProfile,
                    };
                    setAppliedFiltersState(updatedFilters);

                    setModalCheckTrigger((prev) => prev + 1);
                  }}
                />
              )}
              <FreeTrialRestrictionModal
                isOpen={activeModal === "FREE_TRIAL_STARTED"}
                onClose={() => {
                  sessionStorage.removeItem("showFreeTrialStartedPopup");
                  setModalCheckTrigger((prev) => prev + 1);
                }}
                variant="freeTrialStarted"
              />
              <GlobalNotification />
              <PushNotificationPrompt />
              <HoverNotificationStackWrapper />
            </div>
          </SearchContext.Provider>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default ConsumerNewLayout;
